import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col, 
  Spin,
  DatePicker,
  Divider,
  Tabs,
  Switch
} from 'antd';
import {
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BellOutlined,
  PhoneOutlined,
  MailOutlined,
  LinkOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined as PlusIconOutlined,
  EnvironmentOutlined,
  FacebookOutlined,
  TwitterOutlined,
  LinkedinOutlined,
  InstagramOutlined,
  YoutubeOutlined
} from '@ant-design/icons';
import moment from 'moment';
import {
  getTopUtilityConfig,
  updateTopUtilityConfig,
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  reorderAnnouncements
} from '../../api/topUtilityApi';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TopUtilityManagement = () => {
  const [config, setConfig] = useState({
    countdownTitle: 'Event Countdown',
    targetDate: '',
    phones: ['+91-1234567890'],
    emails: ['info@example.com'],
    businessHours: [
      { day: 'Monday-Friday', hours: '9am to 5pm' },
      { day: 'Saturday', hours: '10am to 3pm' },
      { day: 'Sunday', hours: 'Closed' }
    ],
    officeAddress: '',
    socialLinks: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      youtube: ''
    }
  });

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal states
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  // Form states
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    url: '',
    backgroundColor: '#ffffff',
    textColor: '#000000',
    animationSpeed: 'normal',
    isActive: true,
    order: 0
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configRes, announcementsRes] = await Promise.all([
        getTopUtilityConfig(),
        getAnnouncements(true)
      ]);

      if (configRes.success) {
        setConfig({
          ...configRes.data,
          targetDate: configRes.data.targetDate ? new Date(configRes.data.targetDate).toISOString().split('T')[0] : ''
        });
      }

      if (announcementsRes.success) {
        setAnnouncements(announcementsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle config update
  const handleConfigUpdate = async (values) => {
    try {
      setSaving(true);
      const configData = {
        ...config,
        ...values,
        targetDate: values.targetDate ? values.targetDate.format('YYYY-MM-DD') : null
      };
      const result = await updateTopUtilityConfig(configData);
      if (result.success) {
        toast.success('Configuration updated successfully');
        setShowConfigModal(false);
        fetchData();
      } else {
        toast.error('Failed to update configuration');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  // Handle announcement form submit
  const handleAnnouncementSubmit = async (values) => {
    try {
      setSaving(true);
      const formData = {
        ...announcementForm,
        ...values,
        order: editingAnnouncement ? editingAnnouncement.order : announcements.length
      };

      const result = editingAnnouncement
        ? await updateAnnouncement(editingAnnouncement._id, formData)
        : await createAnnouncement(formData);

      if (result.success) {
        toast.success(`Announcement ${editingAnnouncement ? 'updated' : 'created'} successfully`);
        setShowAnnouncementModal(false);
        resetAnnouncementForm();
        fetchData();
      } else {
        toast.error(`Failed to ${editingAnnouncement ? 'update' : 'create'} announcement`);
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error(`Failed to ${editingAnnouncement ? 'update' : 'create'} announcement`);
    } finally {
      setSaving(false);
    }
  };

  // Handle announcement delete
  const handleDeleteAnnouncement = (id) => {
    Modal.confirm({
      title: 'Delete Announcement',
      content: 'Are you sure you want to delete this announcement?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const result = await deleteAnnouncement(id);
          if (result.success) {
            toast.success('Announcement deleted successfully');
            fetchData();
          } else {
            toast.error('Failed to delete announcement');
          }
        } catch (error) {
          console.error('Error deleting announcement:', error);
          toast.error('Failed to delete announcement');
        }
      }
    });
  };

  // Handle edit announcement
  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      url: announcement.url || '',
      backgroundColor: announcement.backgroundColor,
      textColor: announcement.textColor,
      animationSpeed: announcement.animationSpeed,
      isActive: announcement.isActive !== undefined ? announcement.isActive : true,
      order: announcement.order
    });
    setShowAnnouncementModal(true);
  };

  // Reset announcement form
  const resetAnnouncementForm = () => {
    setAnnouncementForm({
      title: '',
      content: '',
      url: '',
      backgroundColor: '#ffffff',
      textColor: '#000000',
      animationSpeed: 'normal',
      isActive: true,
      order: 0
    });
    setEditingAnnouncement(null);
  };

  // Handle reorder announcements
  const handleReorder = async (dragIndex, dropIndex) => {
    const newAnnouncements = [...announcements];
    const draggedItem = newAnnouncements[dragIndex];
    newAnnouncements.splice(dragIndex, 1);
    newAnnouncements.splice(dropIndex, 0, draggedItem);

    // Update order values
    const reorderedAnnouncements = newAnnouncements.map((announcement, index) => ({
      id: announcement._id,
      order: index
    }));

    try {
      const result = await reorderAnnouncements(reorderedAnnouncements);
      if (result.success) {
        setAnnouncements(newAnnouncements);
        toast.success('Announcements reordered successfully');
      } else {
        toast.error('Failed to reorder announcements');
      }
    } catch (error) {
      console.error('Error reordering announcements:', error);
      toast.error('Failed to reorder announcements');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Statistics cards
  const StatCard = ({ title, value, icon, color }) => (
    <Card bordered={false} style={{ backgroundColor: color.bg, borderRadius: '8px' }}>
      <Space direction="vertical" size={0}>
        <Text type="secondary" style={{ fontSize: '14px' }}>{title}</Text>
        <Space size="small">
          <span style={{ fontSize: '24px', color: color.text }}>{icon}</span>
          <Title level={3} style={{ margin: 0, color: color.text }}>{value}</Title>
        </Space>
      </Space>
    </Card>
  );

  // Table columns for announcements
  const columns = [
    {
      title: <span style={{ fontWeight: 500 }}>Sr. No.</span>,
      key: 'index',
      width: 80,
      render: (text, record, index) => (
        <Text style={{ color: '#666', fontSize: '16px' }}>{index + 1}</Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Title</span>,
      dataIndex: 'title',
      key: 'title',
      render: (text) => <Text strong style={{ color: '#333', fontSize: '15px' }}>{text}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Content</span>,
      dataIndex: 'content',
      key: 'content',
      render: (text) => (
        <Text style={{ fontSize: '14px', maxWidth: '300px', display: 'block' }} ellipsis={{ tooltip: text }}>
          {text}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Speed</span>,
      dataIndex: 'animationSpeed',
      key: 'animationSpeed',
      width: 100,
      render: (speed) => (
        <Tag color={speed === 'fast' ? 'red' : speed === 'slow' ? 'blue' : 'green'}>
          {speed}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Actions</span>,
      key: 'actions',
      width: 150,
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditAnnouncement(record)}
            style={{ padding: 0 }}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteAnnouncement(record._id)}
            style={{ padding: 0 }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header Section */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0, fontFamily: "'Rubik', sans-serif" }}>
            Top Header Management
          </Title>
          <Text type="secondary">Manage announcements, contact info, and social media links</Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<SettingOutlined />}
            onClick={() => setShowConfigModal(true)}
            style={{ backgroundColor: '#3C007A' }}
          >
            Social Connect
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              resetAnnouncementForm();
              setShowAnnouncementModal(true);
            }}
          >
            Add Announcement
          </Button>
        </Space>
      </div>

      <Tabs
        defaultActiveKey="social"
        items={[
          {
            key: 'social',
            label: (
              <span>
                <SettingOutlined /> Social Connect
              </span>
            ),
            children: (
              <>
                {/* Statistics Cards */}
                {/* <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                  <Col xs={24} sm={12} lg={6}>
                    <StatCard
                      title="Social Media Links"
                      value={Object.values(config.socialLinks || {}).filter(url => url).length}
                      icon={<LinkOutlined />}
                      color={{ bg: '#f9f0ff', text: '#722ed1' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <StatCard
                      title="Contact Email"
                      value={config.emails && config.emails.length > 0 ? '✓ Set' : 'Not Set'}
                      icon={<MailOutlined />}
                      color={{ bg: '#fff1f0', text: '#f5222d' }}
                    />
                  </Col>
                </Row> */}

                {/* Current Configuration Card */}
                <Card
                  title={<Space><SettingOutlined /> Social Connect</Space>}
                  extra={
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => setShowConfigModal(true)}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Edit
                    </Button>
                  }
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, height: '100%' }}>
                        <Space align="center" style={{ marginBottom: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 6, background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PhoneOutlined style={{ color: '#1890ff' }} />
                          </div>
                          <Text strong>Phone{(config.phones || []).length > 1 ? 's' : ''}</Text>
                        </Space>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          {(config.phones && config.phones.length > 0 ? config.phones : ['Not set']).map((p, i) => (
                            <Text key={i} style={{ display: 'block' }}>{p}</Text>
                          ))}
                        </Space>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, height: '100%' }}>
                        <Space align="center" style={{ marginBottom: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 6, background: '#fff1f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MailOutlined style={{ color: '#f5222d' }} />
                          </div>
                          <Text strong>Email{(config.emails || []).length > 1 ? 's' : ''}</Text>
                        </Space>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          {(config.emails && config.emails.length > 0 ? config.emails : ['Not set']).map((e, i) => (
                            <Text key={i} style={{ display: 'block', wordBreak: 'break-all' }}>{e}</Text>
                          ))}
                        </Space>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, height: '100%' }}>
                        <Space align="center" style={{ marginBottom: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 6, background: '#f6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <EnvironmentOutlined style={{ color: '#52c41a' }} />
                          </div>
                          <Text strong>Office Address</Text>
                        </Space>
                        <Text style={{ display: 'block' }}>{config.officeAddress || 'Not set'}</Text>
                      </div>
                    </Col>
                  </Row>

                  <Divider orientation="left" orientationMargin={0} style={{ margin: '24px 0 16px', color: '#8c8c8c', fontSize: 13, fontWeight: 500 }}>
                    Business Hours
                  </Divider>
                  {(config.businessHours || []).length > 0 ? (
                    <Row gutter={[12, 12]}>
                      {config.businessHours.map((entry, index) => (
                        <Col xs={24} sm={12} md={8} key={index}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            padding: '10px 14px'
                          }}>
                            <Space size={8}>
                              <ClockCircleOutlined style={{ color: '#722ed1' }} />
                              <Text strong>{entry.day}</Text>
                            </Space>
                            <Text type="secondary">{entry.hours}</Text>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <Text type="secondary">Not set</Text>
                  )}

                  <Divider orientation="left" orientationMargin={0} style={{ margin: '24px 0 16px', color: '#8c8c8c', fontSize: 13, fontWeight: 500 }}>
                    Social Media Links
                  </Divider>
                  {(() => {
                    const socialMeta = {
                      facebook: { icon: <FacebookOutlined />, color: '#1877F2', label: 'Facebook' },
                      twitter: { icon: <TwitterOutlined />, color: '#1DA1F2', label: 'Twitter' },
                      linkedin: { icon: <LinkedinOutlined />, color: '#0A66C2', label: 'LinkedIn' },
                      instagram: { icon: <InstagramOutlined />, color: '#E1306C', label: 'Instagram' },
                      youtube: { icon: <YoutubeOutlined />, color: '#FF0000', label: 'YouTube' },
                    };
                    const activeLinks = Object.entries(config.socialLinks || {}).filter(([, url]) => url);
                    if (activeLinks.length === 0) {
                      return <Text type="secondary">No social media links set</Text>;
                    }
                    return (
                      <Space wrap size={12}>
                        {activeLinks.map(([platform, url]) => {
                          const meta = socialMeta[platform] || { icon: <LinkOutlined />, color: '#470172', label: platform };
                          return (
                            <a
                              key={platform}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 14px',
                                borderRadius: 20,
                                border: `1px solid ${meta.color}33`,
                                background: `${meta.color}14`,
                                color: meta.color,
                                fontWeight: 500,
                                textDecoration: 'none'
                              }}
                            >
                              {meta.icon} {meta.label}
                            </a>
                          );
                        })}
                      </Space>
                    );
                  })()}
                </Card>
              </>
            ),
          },
          {
            key: 'announcements',
            label: (
              <span>
                <BellOutlined /> Add Announcement
              </span>
            ),
            children: (
              <>
                {/* Statistics Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                  <Col xs={24} sm={12} lg={8}>
                    <StatCard
                      title="Total Announcements"
                      value={announcements.length}
                      icon={<BellOutlined />}
                      color={{ bg: '#e6f7ff', text: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <StatCard
                      title="Active Announcements"
                      value={announcements.filter(a => a.isActive).length}
                      icon={<BellOutlined />}
                      color={{ bg: '#f6ffed', text: '#52c41a' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <StatCard
                      title="Inactive Announcements"
                      value={announcements.filter(a => !a.isActive).length}
                      icon={<BellOutlined />}
                      color={{ bg: '#fff1f0', text: '#f5222d' }}
                    />
                  </Col>
                </Row>

                {/* Announcements Table */}
                <Card
                  title={<Space><BellOutlined /> Announcements</Space>}
                  extra={
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        resetAnnouncementForm();
                        setShowAnnouncementModal(true);
                      }}
                    >
                      Add New
                    </Button>
                  }
                >
                  <Table
                    columns={columns}
                    dataSource={announcements}
                    rowKey="_id"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} announcements`,
                    }}
                    locale={{
                      emptyText: 'No announcements found. Click "Add Announcement" to create your first one.',
                    }}
                  />
                </Card>
              </>
            ),
          },
        ]}
      />

      {/* Configuration Modal */}
      <Modal
        title={<Space><SettingOutlined /> Edit Configuration</Space>}
        open={showConfigModal}
        onCancel={() => setShowConfigModal(false)}
        footer={null}
        width={820}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', paddingRight: 4 } }}
      >
        <Form
          layout="vertical"
          onFinish={handleConfigUpdate}
          initialValues={{
            ...config,
            targetDate: config.targetDate ? moment(config.targetDate) : null,
          }}
        >
          {/* Contact Information */}
          <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <Space align="center" style={{ marginBottom: 16 }}>
              <PhoneOutlined style={{ color: '#470172' }} />
              <Text strong>Contact Information</Text>
            </Space>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>Phone Numbers</Text>
                <Form.List
                  name="phones"
                  rules={[{
                    validator: async (_, phones) => {
                      if (!phones || phones.filter(p => p && p.trim() !== '').length < 1) {
                        return Promise.reject(new Error('At least one phone number is required'));
                      }
                    }
                  }]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                          <Form.Item
                            {...restField}
                            name={name}
                            rules={[{ required: true, message: 'Phone number is required' }]}
                            style={{ marginBottom: 0, flex: 1 }}
                          >
                            <Input prefix={<PhoneOutlined />} placeholder="+91-1234567890" />
                          </Form.Item>
                          {fields.length > 1 && (
                            <MinusCircleOutlined style={{ color: '#f5222d' }} onClick={() => remove(name)} />
                          )}
                        </Space>
                      ))}
                      <Button type="dashed" size="small" onClick={() => add('')} icon={<PlusIconOutlined />} block>
                        Add Phone Number
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </>
                  )}
                </Form.List>
              </Col>
              <Col xs={24} md={12}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>Email Addresses</Text>
                <Form.List
                  name="emails"
                  rules={[{
                    validator: async (_, emails) => {
                      if (!emails || emails.filter(e => e && e.trim() !== '').length < 1) {
                        return Promise.reject(new Error('At least one email is required'));
                      }
                    }
                  }]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                          <Form.Item
                            {...restField}
                            name={name}
                            rules={[
                              { required: true, message: 'Email is required' },
                              { type: 'email', message: 'Please enter valid email' },
                            ]}
                            style={{ marginBottom: 0, flex: 1 }}
                          >
                            <Input prefix={<MailOutlined />} placeholder="info@example.com" />
                          </Form.Item>
                          {fields.length > 1 && (
                            <MinusCircleOutlined style={{ color: '#f5222d' }} onClick={() => remove(name)} />
                          )}
                        </Space>
                      ))}
                      <Button type="dashed" size="small" onClick={() => add('')} icon={<PlusIconOutlined />} block>
                        Add Email Address
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </>
                  )}
                </Form.List>
              </Col>
            </Row>

            <Form.Item
              label={<Text type="secondary" style={{ fontSize: 13, color: '#000000' }}>Office Address</Text>}
              name="officeAddress"
              style={{ marginTop: 16, marginBottom: 0 }}
            >
              <Input
                prefix={<EnvironmentOutlined />}
                placeholder="123 Business Street, City, State"
                onChange={(e) => setConfig({ ...config, officeAddress: e.target.value })}
              />
            </Form.Item>
          </div>

          {/* Business Hours */}
          <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <Space align="center" style={{ marginBottom: 16 }}>
              <ClockCircleOutlined style={{ color: '#470172' }} />
              <Text strong>Business Hours</Text>
            </Space>

            <Form.List name="businessHours">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={12} key={key} align="middle" style={{ marginBottom: 8 }}>
                      <Col xs={24} sm={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'day']}
                          rules={[{ required: true, message: 'Day is required' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="e.g. Monday-Friday" />
                        </Form.Item>
                      </Col>
                      <Col xs={20} sm={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'hours']}
                          rules={[{ required: true, message: 'Hours are required' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input prefix={<ClockCircleOutlined />} placeholder="e.g. 9am to 5pm or Closed" />
                        </Form.Item>
                      </Col>
                      <Col xs={4} sm={4} style={{ textAlign: 'center' }}>
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    size="small"
                    onClick={() => add({ day: '', hours: '' })}
                    icon={<PlusIconOutlined />}
                    block
                  >
                    Add Row
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          {/* Social Media Links */}
          <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, marginBottom: 8 }}>
            <Space align="center" style={{ marginBottom: 16 }}>
              <LinkOutlined style={{ color: '#470172' }} />
              <Text strong>Social Media Links</Text>
            </Space>

            <Row gutter={16}>
              {Object.keys(config.socialLinks).map((platform) => (
                <Col xs={24} md={12} key={platform}>
                  <Form.Item
                    label={<Text type="secondary" style={{ fontSize: 13 }}>{platform.charAt(0).toUpperCase() + platform.slice(1)}</Text>}
                    name={['socialLinks', platform]}
                  >
                    <Input
                      prefix={<LinkOutlined />}
                      placeholder={`https://${platform}.com/...`}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          socialLinks: {
                            ...config.socialLinks,
                            [platform]: e.target.value,
                          },
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: '8px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowConfigModal(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} style={{ backgroundColor: '#3C007A' }}>
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Announcement Modal */}
      <Modal
        title={
          <Space>
            <BellOutlined />
            {editingAnnouncement ? 'Edit Announcement' : 'Add Announcement'}
          </Space>
        }
        open={showAnnouncementModal}
        onCancel={() => {
          setShowAnnouncementModal(false);
          resetAnnouncementForm();
        }}
        footer={null}
        width={600}
      >
        <Form
          layout="vertical"
          onFinish={handleAnnouncementSubmit}
          initialValues={announcementForm}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please enter title' }]}
          >
            <Input
              placeholder="Enter announcement title"
              maxLength={100}
              onChange={(e) =>
                setAnnouncementForm({ ...announcementForm, title: e.target.value })
              }
            />
          </Form.Item>

          <Form.Item
            label="Content"
            name="content"
            rules={[{ required: true, message: 'Please enter content' }]}
          >
            <TextArea
              rows={3}
              placeholder="Enter announcement content"
              maxLength={500}
              showCount
              onChange={(e) =>
                setAnnouncementForm({ ...announcementForm, content: e.target.value })
              }
            />
          </Form.Item>

          <Form.Item label="URL (Optional)" name="url">
            <Input
              prefix={<LinkOutlined />}
              placeholder="https://example.com"
              onChange={(e) =>
                setAnnouncementForm({ ...announcementForm, url: e.target.value })
              }
            />
          </Form.Item>

          {/* <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Background Color" name="backgroundColor">
                <Input
                  type="color"
                  style={{ height: '40px' }}
                  onChange={(e) =>
                    setAnnouncementForm({
                      ...announcementForm,
                      backgroundColor: e.target.value,
                    })
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Text Color" name="textColor">
                <Input
                  type="color"
                  style={{ height: '40px' }}
                  onChange={(e) =>
                    setAnnouncementForm({ ...announcementForm, textColor: e.target.value })
                  }
                />
              </Form.Item>
            </Col>
          </Row> */}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Animation Speed" name="animationSpeed">
                <Select
                  onChange={(value) =>
                    setAnnouncementForm({ ...announcementForm, animationSpeed: value })
                  }
                >
                  <Select.Option value="slow">Slow</Select.Option>
                  <Select.Option value="normal">Normal</Select.Option>
                  <Select.Option value="fast">Fast</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Status" name="isActive" valuePropName="checked">
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  onChange={(checked) =>
                    setAnnouncementForm({ ...announcementForm, isActive: checked })
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setShowAnnouncementModal(false);
                  resetAnnouncementForm();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingAnnouncement ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TopUtilityManagement;
